import { useEffect, type FC } from 'react';

import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import type { Register } from '../../../../types/helsenorge.helseregistre.internalapi.entities.entities';
import type { HNHelseregistreJS } from '../../../../types/Resources';

import Button from '@helsenorge/designsystem-react/components/Button';
import FormGroup from '@helsenorge/designsystem-react/components/FormGroup';
import Spacer from '@helsenorge/designsystem-react/components/Spacer';
import Textarea from '@helsenorge/designsystem-react/components/Textarea';
import Validation from '@helsenorge/designsystem-react/components/Validation';

import PendingChanges from '@helsenorge/core-framework/pending-changes';
import { setHasPendingChanges, resetPendingChanges } from '@helsenorge/core-framework/pending-changes/pending-changes-state';

import { useAppDispatch } from '../../../store/hooks';
import { submitMessage } from '../register-melding';

import styles from './styles.module.scss';

interface Props {
  meldeFeilRegister: Register;
  resources: HNHelseregistreJS;
  controlHash: string;
  onReset: () => void;
}

type InputForm = {
  meldeFeilTextId: string;
};

const MeldeFeilForm: FC<Props> = ({ meldeFeilRegister, resources, controlHash, onReset }) => {
  const dispatch = useAppDispatch();
  const meldeFeilTextId = 'meldeFeilTextId';

  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors, isDirty },
  } = useForm<InputForm>({ defaultValues: { meldeFeilTextId: '' } });

  const onSubmit = (data: InputForm): void => {
    dispatch(submitMessage(data.meldeFeilTextId, meldeFeilRegister, controlHash));
  };

  useEffect(() => {
    setFocus(meldeFeilTextId);
  }, [setFocus]);

  useEffect(() => {
    if (isDirty) {
      dispatch(setHasPendingChanges());
    } else {
      dispatch(resetPendingChanges());
    }
  }, [dispatch, isDirty]);

  return (
    <>
      <PendingChanges discardChanges={onReset} />
      <form
        onSubmit={handleSubmit((data: InputForm) => {
          dispatch(resetPendingChanges());
          onSubmit(data);
        })}
      >
        <Validation errors={errors} errorTitle={errors[meldeFeilTextId] ? resources.conversationSubmitErrorMessage : undefined}>
          <FormGroup
            legend={resources.melding_label}
            error={errors && errors.meldeFeilTextId ? (errors.meldeFeilTextId.message as string) : undefined}
          >
            {resources.message_label_info}
            <Textarea
              maxCharacters={400}
              textareaId="textarea1"
              {...register(meldeFeilTextId, {
                required: resources.conversationSubmitMessageRequired,
                maxLength: { value: 400, message: resources.melde_feil_over_max_length_error },
              })}
              autoComplete="on"
              defaultValue={''}
            />
          </FormGroup>
        </Validation>
        <p>{resources.message_henvendelse_info}</p>
        <Spacer />
        <div>
          <Button type="submit" className={styles['melde-feil__submit-button']}>
            {resources.conversationSubmitProceedButtonText}
          </Button>
          <Button
            variant="borderless"
            onClick={(): void => {
              navigate('/');
            }}
          >
            {resources.conversationSubmitCancelButtonText}
          </Button>
        </div>
      </form>
    </>
  );
};

export default MeldeFeilForm;
